import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { AddUserSidePanel, UsersList, CompaniesList } from "../../components";
import AdminProject from "../admin-projects/AdminProject";
import styles from "./AdminUsers.module.css";
import { icon, ViewTabPanel } from "../../../../common/components";
import { Flags, isFeatureEnabled, translate } from "../../../../common/providers";
import ViewTabs from "../../../../common/components/tabs/view-tabs/ViewTabs";
import AdminCompanyLogo from "../admin-company-logo/AdminCompanyLogo";

export default function AdminUsers({
	companies = [],
	getCompanyUsers,
	onAddUser,
	onChangeLock,
	onChangeRole,
	COMPANY_ROLES,
	companyIdToUser,
	getCompanyProjects,
	onChangeArchive,
	onChangeName,
	companyIdToProject,
}) {
	const [selectedTabIndex, setSelectedTabIndex] = useState(0);
	const [selectedCompany, setSelectedCompany] = useState(null);
	const [isAdding, setIsAdding] = useState(false);
	const loggedIncompanyId = useSelector(({ context }) => context.company?.id);
	const tabs = useMemo(() => {
		let t = [
			{ label: translate("admin.tab.title.user-directory"), iconName: icon.faUserFriends },
			{ label: translate("admin.tab.title.projects-management"), iconName: icon.faCompassDrafting },
		];
		if (isFeatureEnabled(Flags.COMPANY_LOGO)) {
			t = [
				...t,
				{
					label: translate("admin.company-logo.title"),
					iconName: icon.faImage,
					disabled: !isFeatureEnabled(Flags.COMPANY_LOGO),
				},
			];
		}
		return t;
	}, []);
	const handleSelectCompany = useCallback(
		(company) => {
			if (!selectedCompany || company?.id !== selectedCompany?.id) {
				setSelectedCompany(company);
				getCompanyUsers({ company });
			}
		},
		[selectedCompany, getCompanyUsers]
	);
	const findCompanyWithAdminUserRole = useCallback(
		(dataTree) => {
			for (const company of dataTree) {
				if (company.userRole === "ADMIN" && company.id === loggedIncompanyId) {
					return company;
				}
				if (company.children) {
					const innerCompanyId = findCompanyWithAdminUserRole(company.children);
					if (innerCompanyId) {
						return innerCompanyId;
					}
				}
			}
			return null;
		},
		[loggedIncompanyId]
	);
	useEffect(() => {
		if (!selectedCompany && Array.isArray(companies) && companies.length > 0) {
			handleSelectCompany(findCompanyWithAdminUserRole(companies));
		}
	}, [selectedCompany, companies, handleSelectCompany, companyIdToUser, findCompanyWithAdminUserRole]);

	const handleChangeRole = ({ role, user }) => {
		if (!role || !user || !selectedCompany || user.companyRole === role) {
			return;
		}
		onChangeRole({ companyId: selectedCompany?.id, userId: user.id, role });
	};

	const handleChangeLock = ({ user }) => {
		if (!user) {
			return;
		}
		onChangeLock({ companyId: selectedCompany?.id, userId: user.id, isLocked: user.locked });
	};

	return (
		<>
			<div className={styles.container}>
				<ViewTabs
					noShadow
					selectedTab={selectedTabIndex}
					tabs={tabs}
					onChange={(_, v) => setSelectedTabIndex(v)}
				/>
				<div className={styles.layout}>
					<div className={styles["left-panel"]}>
						<CompaniesList
							companies={companies}
							selectedCompany={selectedCompany}
							selectedCompanyUserCount={Array.isArray(companyIdToUser) ? companyIdToUser.length : null}
							onClickRow={handleSelectCompany}
						/>
					</div>
					<div className={styles["middle-panel"]}>
						<ViewTabPanel noPadding index={0} value={selectedTabIndex}>
							<UsersList
								COMPANY_ROLES={COMPANY_ROLES}
								companyId={selectedCompany?.id}
								isAdding={isAdding}
								users={companyIdToUser}
								onChangeLock={handleChangeLock}
								onChangeRole={handleChangeRole}
								onSetIsAdding={setIsAdding}
							/>
						</ViewTabPanel>
						<ViewTabPanel noPadding index={1} value={selectedTabIndex}>
							<AdminProject
								companyIdToProject={companyIdToProject}
								getCompanyProjects={getCompanyProjects}
								selectedCompany={selectedCompany}
								onChangeArchive={onChangeArchive}
								onChangeName={onChangeName}
							/>
						</ViewTabPanel>
						<ViewTabPanel noPadding index={2} value={selectedTabIndex}>
							<AdminCompanyLogo />
						</ViewTabPanel>
					</div>
				</div>
			</div>
			<AddUserSidePanel
				open={isAdding}
				onAdd={({ payload }) => {
					onAddUser({ companyId: selectedCompany?.id, payload });
					setIsAdding(false);
				}}
				onClose={() => setIsAdding(false)}
			/>
		</>
	);
}
